// src/pages/affiliate/AffiliateDashboardPage.tsx
// The partner's home: who am I as an affiliate, how am I doing, what do I do
// next. Onboards a person with no affiliate profile yet; otherwise shows
// status, balance, stats, tier progress and recent activity.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import {
  useAffiliate,
  useAffiliateCommissionTier,
  useAffiliateConversion,
  useAffiliatePayout,
  useI18n,
} from '@react/hooks';
import { Button, Card, CardContent, Alert } from '@react/components';
import { authStore } from '@core/stores';
import { useScope } from '@/hooks/useScope';
import type { Affiliate, CommissionTier } from '@core/types/affiliate';
import { AffiliateStatusBadge, ConversionStatusBadge, PayoutStatusBadge, TierStatusBadge } from '@/features/affiliate/badges';
import { ProfileDialog } from '@/features/affiliate/ProfileDialog';
import { PayoutRequestDialog } from '@/features/affiliate/PayoutRequestDialog';
import {
  clearStoredAffiliateId,
  errMessage,
  fmtDate,
  formatMoney,
  getStoredAffiliateId,
  listParams,
  setStoredAffiliateId,
} from '@/features/affiliate/format';

export const AffiliateDashboardPage = observer(() => {
  const { t, locale } = useI18n();
  useScope();
  const store = useAffiliate();
  const tierStore = useAffiliateCommissionTier();
  const conversionStore = useAffiliateConversion();
  const payoutStore = useAffiliatePayout();
  const profileId = authStore.activeProfileId;

  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [tier, setTier] = useState<CommissionTier | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [statsPeriodDays, setStatsPeriodDays] = useState(30);
  const [stats, setStats] = useState<{ clicks: number; conversions: number; conversionRate: string; earned: string } | null>(null);

  const load = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const item = await store.GetAffiliate(id);
      setAffiliate(item);
      if (item.tier?.id) {
        try {
          setTier(await tierStore.GetCommissionTier(item.tier.id));
        } catch {
          setTier(null);
        }
      }
      conversionStore.ListConversions(listParams({ limit: 5 }, { affiliate_id: id })).catch(() => {});
      payoutStore.ListPayouts(listParams({ limit: 5 }, { affiliate_id: id })).catch(() => {});
      tierStore.ListCommissionTiers(listParams({ limit: 20 })).catch(() => {});
    } catch (e) {
      clearStoredAffiliateId(profileId);
      setAffiliate(null);
      setError(errMessage(e, t('affiliate.dashboard.loadError')));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const id = getStoredAffiliateId(profileId);
    if (id) {
      load(id);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  useEffect(() => {
    if (!affiliate?.entity?.id) return;
    const end = new Date();
    const start = new Date(end.getTime() - statsPeriodDays * 24 * 60 * 60 * 1000);
    store
      .GetAffiliateStats(affiliate.entity.id)
      .then((res) => {
        const s = res.stats;
        if (!s) return;
        setStats({
          clicks: s.clicks,
          conversions: s.conversions,
          conversionRate: s.conversion_rate,
          earned: formatMoney(s.earned, locale),
        });
      })
      .catch(() => setStats(null));
    void start;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliate?.entity?.id, statsPeriodDays]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        <div className="h-24 animate-pulse rounded-md bg-muted" />
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="mx-auto max-w-lg space-y-4 p-6">
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <h1 className="text-lg font-semibold text-foreground">{t('affiliate.dashboard.onboardTitle')}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('affiliate.dashboard.onboardHint')}</p>
          {error && <Alert variant="error" className="mt-3 text-left">{error}</Alert>}
          <Button className="mt-4" onClick={() => setApplyOpen(true)}>
            {t('affiliate.profile.apply')}
          </Button>
        </div>
        <ProfileDialog
          open={applyOpen}
          onClose={() => setApplyOpen(false)}
          affiliate={null}
          onSaved={(saved) => {
            if (saved.entity?.id) {
              setStoredAffiliateId(profileId, saved.entity.id);
              load(saved.entity.id);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-semibold text-foreground">{affiliate.display_name}</h1>
            <AffiliateStatusBadge status={affiliate.status} />
            {tier && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                {tier.icon && <span aria-hidden="true">{tier.icon}</span>}
                {tier.name}
                <TierStatusBadge status={tier.status} />
              </span>
            )}
          </div>
          {affiliate.description && <p className="mt-1 text-sm text-muted-foreground">{affiliate.description}</p>}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
            {t('affiliate.profile.edit')}
          </Button>
          <Button size="sm" onClick={() => setPayoutOpen(true)}>
            {t('affiliate.payouts.request')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{t('affiliate.dashboard.availableBalance')}</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{formatMoney(affiliate.available_balance, locale)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{t('affiliate.dashboard.totalEarned')}</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{formatMoney(affiliate.total_earned, locale)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{t('affiliate.dashboard.totalPaid')}</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{formatMoney(affiliate.total_paid, locale)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">{t('affiliate.dashboard.activeCampaigns')}</p>
          <p className="mt-1 text-lg font-semibold text-foreground">{affiliate.active_campaigns_count}</p>
        </CardContent></Card>
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">{t('affiliate.dashboard.performance')}</h2>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setStatsPeriodDays(d)}
                className={`rounded-md px-2 py-1 text-xs ${statsPeriodDays === d ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {t('affiliate.dashboard.days', { count: d })}
              </button>
            ))}
          </div>
        </div>
        {stats ? (
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
            <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.clicks')}</p><p className="font-medium text-foreground">{stats.clicks}</p></div>
            <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.conversions')}</p><p className="font-medium text-foreground">{stats.conversions}</p></div>
            <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.conversionRate')}</p><p className="font-medium text-foreground">{stats.conversionRate || '0'}%</p></div>
            <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.earned')}</p><p className="font-medium text-foreground">{stats.earned}</p></div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">{t('common.loading')}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Link to="/affiliate/offers" className="rounded-lg border border-border p-4 hover:bg-muted">
          <p className="font-medium text-foreground">{t('affiliate.dashboard.browseOffers')}</p>
          <p className="text-sm text-muted-foreground">{t('affiliate.dashboard.browseOffersHint')}</p>
        </Link>
        <Link to="/affiliate/campaigns" className="rounded-lg border border-border p-4 hover:bg-muted">
          <p className="font-medium text-foreground">{t('affiliate.dashboard.myCampaigns')}</p>
          <p className="text-sm text-muted-foreground">{t('affiliate.dashboard.myCampaignsHint')}</p>
        </Link>
        <Link to="/affiliate/payouts" className="rounded-lg border border-border p-4 hover:bg-muted">
          <p className="font-medium text-foreground">{t('affiliate.dashboard.myPayouts')}</p>
          <p className="text-sm text-muted-foreground">{t('affiliate.dashboard.myPayoutsHint')}</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border p-4">
          <h2 className="mb-2 text-sm font-semibold text-foreground">{t('affiliate.dashboard.recentConversions')}</h2>
          {conversionStore.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('affiliate.conversions.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {conversionStore.items.map((c) => (
                <li key={c.entity?.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{c.goal || t('affiliate.conversions.title')}</span>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    {formatMoney(c.payout, locale)}
                    <ConversionStatusBadge status={c.status} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-lg border border-border p-4">
          <h2 className="mb-2 text-sm font-semibold text-foreground">{t('affiliate.dashboard.recentPayouts')}</h2>
          {payoutStore.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('affiliate.payouts.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {payoutStore.items.map((p) => (
                <li key={p.entity?.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{fmtDate(p.created_at, locale)}</span>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    {formatMoney(p.amount, locale)}
                    <PayoutStatusBadge status={p.status} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {tierStore.items.length > 0 && (
        <div className="rounded-lg border border-border p-4">
          <h2 className="mb-2 text-sm font-semibold text-foreground">{t('affiliate.dashboard.tiers')}</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {tierStore.items
              .slice()
              .sort((a, b) => a.level - b.level)
              .map((tr) => (
                <div
                  key={tr.entity?.id}
                  className={`rounded-md border p-2 text-center text-xs ${tr.entity?.id === affiliate.tier?.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                >
                  <p className="font-medium text-foreground">{tr.name}</p>
                  <p className="text-muted-foreground">{t('affiliate.dashboard.minConversions', { count: tr.min_conversions })}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      <ProfileDialog open={editOpen} onClose={() => setEditOpen(false)} affiliate={affiliate} onSaved={(saved) => setAffiliate(saved)} />
      <PayoutRequestDialog
        open={payoutOpen}
        onClose={() => setPayoutOpen(false)}
        affiliate={affiliate}
        onRequested={() => affiliate.entity?.id && load(affiliate.entity.id)}
      />
    </div>
  );
});
